<template>
    <bs-modal :id="id" type="modal-dialog-right">
        <template #header>
            <div class="tp-drawer-header">
                <button
                    type="button"
                    class="tp-drawer-close"
                    :aria-label="$t('common.close') || 'Fermer'"
                    @click="cancel"
                >
                    <i class="fal fa-times"></i>
                </button>
                <div v-if="post" class="tp-drawer-head-body">
                    <span class="tp-drawer-ref">#{{ post.reference }}</span>
                    <h2 class="tp-drawer-title">{{ post.title }}</h2>
                    <div class="tp-drawer-meta">
                        <ButtonEditStatus variant="v2" :post="post" />
                        <ButtonEditPriority variant="v2" :post="post" />
                    </div>
                </div>
                <div v-else class="tp-drawer-head-body">
                    <span class="tp-drawer-ref tp-skeleton tp-skeleton--ref"></span>
                    <span class="tp-skeleton tp-skeleton--title"></span>
                </div>
            </div>
        </template>

        <div v-if="post" class="tp-drawer-body">
            <div class="tp-drawer-toolbar">
                <button
                    type="button"
                    class="tp-tb-btn"
                    @click="editTicket"
                >
                    <IconWrite class="tp-tb-ic" />
                    <span>{{ $t('post.page.button.edit') }}</span>
                </button>
                <ButtonRemind variant="preview" :post="post" />
                <ButtonExportPdf variant="preview" :post="post" />
            </div>

            <PostPreviewInfo :post="post" />
            <PostPreviewAnswers :post="post" />
            <PostPreviewAttachments :post="post" />
        </div>

        <template #footer>
            <button
                type="button"
                class="tp-drawer-cta"
                :disabled="!post"
                @click="showTicket"
            >
                {{ $t('post.page.preview.display') }}
                <i class="fal fa-arrow-right"></i>
            </button>
        </template>
    </bs-modal>
</template>

<script>
import BsModal from '@/components/UI/BsModal'
import ModalMixin from '@/components/UI/ModalMixin'
import ButtonEditStatus from '../../UI/ButtonEditStatus'
import ButtonRemind from '../../UI/ButtonRemind'
import ButtonExportPdf from '../../UI/ButtonExportPdf'
import IconWrite from '@/components/icons/iconWrite'
import { mapState } from 'vuex'
import PostPreviewInfo from './PostPreviewInfo'
import PostPreviewAnswers from './PostPreviewAnswers'
import PostPreviewAttachments from './PostPreviewAttachments'
import ButtonEditPriority from './ButtonEditPriority'

export default {
    name: 'PostPreviewModal',
    mixins: [ModalMixin],
    components: {
        PostPreviewAnswers,
        BsModal,
        ButtonRemind,
        PostPreviewInfo,
        ButtonEditStatus,
        PostPreviewAttachments,
        ButtonExportPdf,
        ButtonEditPriority,
        IconWrite
    },
    data: function () {
        return {
            id: 'postPreview'
        }
    },
    methods: {
        trigger: function () {
            this.modal.show()
        },
        cancel: function () {
            this.modal.hide()
        },
        showTicket: function () {
            if (!this.post) return
            this.cancel()
            this.$router.push(`/posts/${this.post.id}`)
        },
        editTicket: function () {
            if (!this.post) return
            this.cancel()
            this.$router.push(`/post/${this.post.id}/edit`)
        }
    },
    mounted() {
        this.create(this.id, {
            keyboard: true
        })
    },
    computed: {
        ...mapState('post', {
            post: (state) => state.post
        })
    }
}
</script>

<style scoped>
::v-deep .modal-dialog-right {
    max-width: 520px;
    width: 100%;
}

::v-deep .modal-content {
    background: var(--v2-surface-1, #f8fafc);
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2, #334155);
}

::v-deep .modal-body {
    padding: 0 !important;
}

::v-deep .modal-footer {
    padding: 16px 20px;
    border-top: 1px solid var(--v2-border, #e2e8f0);
    background: var(--v2-surface-0, #fff);
    position: sticky;
    bottom: 0;
    z-index: 1055;
}

/* ---- Header ---- */
.tp-drawer-header {
    position: relative;
    padding: 22px 56px 18px 24px;
    background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
    color: #fff;
}

.tp-drawer-close {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 0;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 120ms ease;
}

.tp-drawer-close:hover {
    background: rgba(255, 255, 255, 0.22);
}

.tp-drawer-close i {
    font-size: 0.95rem;
}

.tp-drawer-head-body {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.tp-drawer-ref {
    display: inline-block;
    width: fit-content;
    padding: 3px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.18);
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.tp-drawer-title {
    margin: 2px 0 0;
    font-size: 1.15rem;
    line-height: 1.3;
    font-weight: 700;
    letter-spacing: -0.01em;
    word-break: break-word;
}

.tp-drawer-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
}

/* ---- Body ---- */
.tp-drawer-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
}

.tp-drawer-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px;
    background: var(--v2-surface-0, #fff);
    border: 1px solid var(--v2-border, #e2e8f0);
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.tp-tb-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    border-radius: 8px;
    border: 1px solid var(--v2-border, #e2e8f0);
    background: var(--v2-surface-0, #fff);
    color: var(--v2-text-2, #334155);
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 120ms ease;
}

.tp-tb-btn:hover {
    border-color: var(--v2-primary, #0891b2);
    color: var(--v2-primary, #0891b2);
    background: var(--v2-primary-soft, rgba(8, 145, 178, 0.08));
}

.tp-tb-ic {
    width: 14px;
    height: 14px;
    color: currentColor;
}

/* ---- Footer CTA ---- */
.tp-drawer-cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 42px;
    border: 0;
    border-radius: 10px;
    background: var(--v2-primary, #0891b2);
    color: #fff;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 120ms ease, transform 120ms ease;
}

.tp-drawer-cta:hover:not(:disabled) {
    background: var(--v2-primary-hover, #0e7490);
}

.tp-drawer-cta:active:not(:disabled) {
    transform: translateY(1px);
}

.tp-drawer-cta:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.tp-drawer-cta i {
    font-size: 0.85rem;
}

/* ---- Skeleton ---- */
.tp-skeleton {
    display: inline-block;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 6px;
    animation: tp-pulse 1.2s ease-in-out infinite;
}

.tp-skeleton--ref {
    width: 80px;
    height: 18px;
}

.tp-skeleton--title {
    width: 70%;
    height: 20px;
    margin-top: 4px;
}

@keyframes tp-pulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 0.85; }
}

/* ---- Responsive ---- */
@media (max-width: 640px) {
    ::v-deep .modal-dialog-right {
        max-width: 100%;
    }

    .tp-drawer-header {
        padding: 18px 52px 16px 18px;
    }

    .tp-drawer-title {
        font-size: 1.05rem;
    }

    .tp-drawer-body {
        padding: 16px;
    }
}

/* ---- Scrollbar ---- */
::v-deep .modal-body::-webkit-scrollbar {
    width: 6px;
}

::v-deep .modal-body::-webkit-scrollbar-track {
    background: transparent;
}

::v-deep .modal-body::-webkit-scrollbar-thumb {
    background: var(--v2-border-strong, #cbd5e1);
    border-radius: 999px;
}
</style>
